import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { VENUE_EVENTS, type Branch, type MenuVersionStatus } from '@/lib/shared-types';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableEmpty, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { MENU_VERSIONS } from './menuGovernanceRoutes';
import { useMenuVersionsQuery, invalidateMenuVersion, type MenuVersionsQuery } from './menuGovernanceApi';
import { useAppDispatch } from '@/Redux/store';
import { formatDateTime } from './lib';
import VersionDetail from './VersionDetail';

const STATUSES: MenuVersionStatus[] = [
  'DRAFT',
  'IN_REVIEW',
  'APPROVED',
  'SCHEDULED',
  'PUBLISHED',
  'REJECTED',
  'CANCELLED',
  'ROLLED_BACK',
];

/**
 * Screen 1 — every draft, scheduled, live and superseded version, newest
 * first. Status is a `Badge`, not a column of text: `toneForStatus` reads
 * `MenuVersionStatus` straight off `@/components/ui/badge`'s status table, so
 * scanning the colour is enough to tell a live version from a dead one without
 * reading the word.
 */
export default function VersionsPanel() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { can } = usePermission();

  const canDraft = can('menu_governance.draft');

  const [status, setStatus] = useState<string>('');
  const [branchId, setBranchId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const query: MenuVersionsQuery = {
    page,
    limit: 25,
    ...(status ? { status: status as MenuVersionStatus } : {}),
    ...(branchId ? { branchId } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  };
  const { data, isFetching, isError, refetch } = useMenuVersionsQuery(query);

  // `GET /branches` is `branches.view`, a different key from this screen's
  // own `menu_governance.view` — a tenant admin holds both, but the filter
  // and the create-draft picker degrade to "no branches offered" rather than
  // 403ing someone who can read versions but not the branch directory.
  const canReadBranches = can('branches.view');
  const branchesQuery = useApi<unknown>(canReadBranches ? '/branches' : null, { limit: 200 });
  const branches = useMemo(() => rowsOf<Branch>(branchesQuery.data).rows, [branchesQuery.data]);

  const filtered = Boolean(status || branchId || search.trim());
  const clearFilters = () => {
    setStatus('');
    setBranchId('');
    setSearch('');
    setPage(1);
  };

  const reload = () => {
    refetch();
    dispatch(invalidateMenuVersion());
  };

  /**
   * `RealtimeService.onMenuVersionEvent` emits one of these six per branch a
   * version TARGETS, ungated on the wire — see `menuGovernanceRoutes.ts` for
   * why (no price rides along, so it is safe for a cashier's till to hear).
   * No `rooms` passed, so `useRealtime` defaults to the header's active
   * branch: the same default every other screen in this dashboard uses, and
   * the honest limit that comes with it — a version targeting only OTHER
   * branches will not refetch this list live, only on the next manual reload
   * or the poll a future `keepUnusedDataFor` revalidation performs. Six calls
   * rather than a loop: hooks cannot be called conditionally or dynamically.
   */
  useRealtime<unknown>(VENUE_EVENTS.MENU_VERSION_SUBMITTED, reload);
  useRealtime<unknown>(VENUE_EVENTS.MENU_VERSION_APPROVED, reload);
  useRealtime<unknown>(VENUE_EVENTS.MENU_VERSION_REJECTED, reload);
  useRealtime<unknown>(VENUE_EVENTS.MENU_VERSION_SCHEDULED, reload);
  useRealtime<unknown>(VENUE_EVENTS.MENU_VERSION_PUBLISHED, reload);
  useRealtime<unknown>(VENUE_EVENTS.MENU_VERSION_ROLLED_BACK, reload);
  useRealtimeResync(reload);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('menuGovernance.versions.filterStatus')} controlId="mg-status">
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className="w-44"
            >
              <option value="">{t('menuGovernance.versions.filterAllStatuses')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`menuGovernance.status.${s}`)}
                </option>
              ))}
            </Select>
          </Field>

          {branches.length > 0 && (
            <Field label={t('menuGovernance.versions.filterBranch')} controlId="mg-branch">
              <Select
                value={branchId}
                onChange={(e) => {
                  setBranchId(e.target.value);
                  setPage(1);
                }}
                className="w-44"
              >
                <option value="">{t('menuGovernance.versions.filterAllBranches')}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field label={t('menuGovernance.versions.search')} controlId="mg-search">
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t('menuGovernance.versions.search')}
              className="w-56"
            />
          </Field>

          {filtered && (
            <Button variant="outline" onClick={clearFilters}>
              {t('menuGovernance.versions.clearFilters')}
            </Button>
          )}
        </div>

        {canDraft && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="me-1.5 h-4 w-4" />
            {t('menuGovernance.versions.newDraft')}
          </Button>
        )}
      </div>

      <TableWrap>
        <Table>
          <THead>
            <TR>
              <TH>{t('menuGovernance.versions.columnVersion')}</TH>
              <TH>{t('menuGovernance.versions.columnName')}</TH>
              <TH>{t('menuGovernance.versions.columnStatus')}</TH>
              <TH>{t('menuGovernance.versions.columnBranches')}</TH>
              <TH>{t('menuGovernance.versions.columnCreatedBy')}</TH>
              <TH>{t('menuGovernance.versions.columnEffective')}</TH>
              <TH className="text-end">{t('menuGovernance.versions.columnRules')}</TH>
            </TR>
          </THead>
          <tbody>
            {isFetching && !data ? (
              <tr>
                <TD colSpan={7}>
                  <LoadingState />
                </TD>
              </tr>
            ) : isError ? (
              <tr>
                <TD colSpan={7}>
                  <ErrorState message={t('menuGovernance.versions.errorTitle')} onRetry={reload} />
                </TD>
              </tr>
            ) : !data?.items.length ? (
              <TableEmpty colSpan={7}>
                <EmptyState
                  title={t(
                    filtered
                      ? 'menuGovernance.versions.emptyFilteredTitle'
                      : 'menuGovernance.versions.emptyTitle',
                  )}
                  hint={t(
                    filtered
                      ? 'menuGovernance.versions.emptyFilteredHint'
                      : 'menuGovernance.versions.emptyHint',
                  )}
                  action={
                    filtered ? (
                      <Button variant="outline" onClick={clearFilters}>
                        {t('menuGovernance.versions.clearFilters')}
                      </Button>
                    ) : canDraft ? (
                      <Button onClick={() => setCreating(true)}>
                        {t('menuGovernance.versions.newDraft')}
                      </Button>
                    ) : undefined
                  }
                />
              </TableEmpty>
            ) : (
              data.items.map((v) => (
                <TR key={v.id} className="cursor-pointer" onClick={() => setOpenId(v.id)}>
                  <TD className="tabular-nums">#{v.versionNo}</TD>
                  <TD className="max-w-xs truncate font-medium">{v.name}</TD>
                  <TD>
                    <Badge tone={toneForStatus(v.status)}>{t(`menuGovernance.status.${v.status}`)}</Badge>
                  </TD>
                  <TD>
                    {v.targets.length === 0 ? (
                      '—'
                    ) : (
                      <>
                        {v.targets[0].branch.name}
                        {v.targets.length > 1 && (
                          <span className="text-ink-muted">
                            {' '}
                            {t('menuGovernance.versions.moreBranches', {
                              count: v.targets.length - 1,
                            })}
                          </span>
                        )}
                      </>
                    )}
                  </TD>
                  <TD>{v.createdBy ? [v.createdBy.firstName, v.createdBy.lastName].filter(Boolean).join(' ') || '—' : '—'}</TD>
                  <TD className="tabular-nums">
                    {v.effectiveFrom
                      ? formatDateTime(v.effectiveFrom, i18n.language)
                      : t('menuGovernance.versions.noEffectiveDate')}
                  </TD>
                  <TD className="text-end tabular-nums">{v._count.rules}</TD>
                </TR>
              ))
            )}
          </tbody>
        </Table>
      </TableWrap>

      {data && data.pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ‹
          </Button>
          <span className="tabular-nums text-ink-muted">
            {page} / {data.pages}
          </span>
          <Button variant="outline" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>
            ›
          </Button>
        </div>
      )}

      {creating && (
        <CreateVersionDialog
          branches={branches}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            reload();
            setOpenId(id);
          }}
        />
      )}

      {openId && (
        <VersionDetail
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

function CreateVersionDialog({
  branches,
  onClose,
  onCreated,
}: {
  branches: Branch[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const nameError = submitted && !name.trim() ? t('menuGovernance.versions.createNameRequired') : undefined;
  const branchError =
    submitted && branchIds.length === 0 ? t('menuGovernance.versions.createBranchesRequired') : undefined;

  const toggle = (id: string) =>
    setBranchIds((cur) => (cur.includes(id) ? cur.filter((b) => b !== id) : [...cur, id]));

  const save = async () => {
    setSubmitted(true);
    if (!name.trim() || branchIds.length === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post(MENU_VERSIONS, {
        name: name.trim(),
        notes: notes.trim() || undefined,
        branchIds,
      });
      toast.success(t('menuGovernance.toast.created'));
      onCreated(data.id as string);
    } catch {
      toast.error(t('menuGovernance.refusal.genericBody'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('menuGovernance.versions.createTitle')}
      className="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('menuGovernance.editor.cancel')}
          </Button>
          <Button onClick={save} disabled={saving}>
            {t('menuGovernance.versions.createSubmit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('menuGovernance.versions.createName')} error={nameError} required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('menuGovernance.versions.createNamePlaceholder')}
            autoFocus
          />
        </Field>
        <Field label={t('menuGovernance.versions.createNotes')}>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('menuGovernance.versions.createNotesPlaceholder')}
            rows={2}
          />
        </Field>
        <Field
          label={t('menuGovernance.versions.createBranches')}
          hint={t('menuGovernance.versions.createBranchesHint')}
          error={branchError}
          required
        >
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border p-2">
            {branches.length === 0 ? (
              <p className="p-2 text-sm text-ink-muted">—</p>
            ) : (
              branches.map((b) => (
                <label
                  key={b.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-hover"
                >
                  <input
                    type="checkbox"
                    checked={branchIds.includes(b.id)}
                    onChange={() => toggle(b.id)}
                    className="h-4 w-4 shrink-0 accent-[var(--accent-solid)]"
                  />
                  <span className="text-sm">{b.name}</span>
                </label>
              ))
            )}
          </div>
        </Field>
      </div>
    </Dialog>
  );
}
